import { Loader, useModal } from '@payright/web-components';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { ContractForm } from './contract-form';
import { ContractInfo } from './contract-info';
import { ContractModal } from './contract-modal';
import { consentToPlan } from '../../../reducers/plan-slice';
import type { AppThunkDispatch } from '../../../util/store';
import { AlertSliceError } from '../../../components/alert-slice-error';
import { useAppStepNavigation } from '../../../util/hooks/useAppStepNavigation';
import { FeatureFlags, useFeatures } from '../../../providers/features-provider';

export const ContractContent = () => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const { goToStep } = useAppStepNavigation();
  const { openModal, dismissAllModals } = useModal();

  const {
    flagsConfig: { [FeatureFlags.AVAILABLE_CREDIT]: availableCreditFeatureFlag },
  } = useFeatures();

  const {
    checkout,
    loading: checkoutLoading,
    paymentDetails,
  } = useSelector((state: RootState) => state.checkout);

  const {
    customerDetails,
    accountDetails,
    addressDetails,
    loading: customerLoading,
  } = useSelector((state: RootState) => state.customer);

  const {
    merchantRates,
    storeConfig,
    loading: merchantRatesLoading,
  } = useSelector((state: RootState) => state.auth);

  const {
    plan,
    loading: planLoading,
    ...planError
  } = useSelector((state: RootState) => state.plan);

  const handleConsentToPlan = () => {
    dispatch(consentToPlan(checkout!.id!)).then((nextStep) => {
      if (nextStep) goToStep(nextStep);
    });
  };

  const merchantStoreName = storeConfig?.name;

  // check for plan = null as we only want to show loader during initial load of plan state
  if (
    checkoutLoading ||
    (planLoading && plan === null) ||
    merchantRatesLoading ||
    customerLoading
  ) {
    return <Loader text="Loading..." />;
  }

  if (plan === null || storeConfig === null || customerDetails === null) {
    return <Loader text="Initialising..." />;
  }

  const handleLaunchModal = () => {
    openModal(
      <ContractModal
        address={addressDetails}
        currentDate={new Date()}
        customer={customerDetails}
        handleSubmit={handleConsentToPlan}
        onDecline={dismissAllModals}
        isLoading={planLoading}
        plan={plan}
        store={storeConfig}
      />
    );
  };

  const withinAvailableCredit = Boolean(availableCreditFeatureFlag && plan?.withinAvailableCredit);

  return (
    <>
      <ContractInfo
        merchantStoreName={merchantStoreName}
        withinAvailableCredit={withinAvailableCredit}
      />
      <ContractForm
        accountKeepingFee={plan?.accountKeepingFee}
        establishmentFee={plan?.establishmentFee}
        loanAmount={plan?.loanAmount}
        onSubmit={withinAvailableCredit ? handleConsentToPlan : handleLaunchModal}
        paymentProcessingFee={merchantRates?.otherFees?.paymentProcessingFee}
        paymentPeriod={plan?.numberOfRepayments}
        repaymentAmount={plan?.repaymentAmount}
        repaymentFrequency={plan?.repaymentFrequency}
        withinAvailableCredit={withinAvailableCredit}
        approvedLimit={accountDetails?.approvedLimit}
        availableCredit={accountDetails?.availableCredit}
        totalCreditAmount={plan?.totalCreditAmount}
        isLoading={planLoading}
        alert={<AlertSliceError slice={planError} />}
      />
    </>
  );
};
